"use client";

import Image from "next/image";
import { useState } from "react";
import { DeviceStage, IpadFrame } from "./device-frames";
import { useSelfDemo } from "./use-self-demo";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Card 1's screen: the multi-branch dashboard on an iPad.
 *
 * Numbers are illustrative but shaped like the real CMS dashboard (net sales,
 * order count, new members, sales by day). Picking a branch swaps the whole
 * set — that switch *is* the pitch: one place to see every shop.
 *
 * When the screen comes into view it demonstrates itself: it keeps stepping
 * around the branches — สีลม, อโศก, ท่าพระ, back to ทุกสาขา — for as long as
 * the card is in front. The switching *is* the pitch here, and a single pass
 * would leave a still image for anyone who arrives a beat later, so this one
 * runs on a loop where the other two screens play once. It stops dead the
 * moment the reader touches a chip, and doesn't run at all under
 * `prefers-reduced-motion`.
 *
 * Palette: the app chrome commits to the brand pair — deep blue #0060A8 owns
 * the header and the hero KPI, yellow #FFCC33 marks "you are here" (the
 * active branch chip, the biggest bar, the live dot). Yellow never carries
 * text on white; it's always a fill under deep-blue ink.
 */

type Branch = {
  id: string;
  /** Key under `home.mocks.dashboard.branches`. */
  labelKey: string;
  /** ยอดขายวันนี้ */
  sales: string;
  delta: string;
  orders: string;
  members: string;
  /** Bar heights, จ→อา, as a share of the plot. */
  week: number[];
};

const BRANCHES: Branch[] = [
  {
    id: "all",
    labelKey: "all",
    sales: "388,350",
    delta: "+12.4%",
    orders: "1,204",
    members: "86",
    week: [0.52, 0.63, 0.48, 0.71, 0.86, 1, 0.79],
  },
  {
    id: "silom",
    labelKey: "silom",
    sales: "125,680",
    delta: "+8.1%",
    orders: "412",
    members: "31",
    week: [0.44, 0.58, 0.4, 0.66, 0.78, 1, 0.62],
  },
  {
    id: "asok",
    labelKey: "asok",
    sales: "98,540",
    delta: "+4.6%",
    orders: "336",
    members: "22",
    week: [0.62, 0.5, 0.55, 0.44, 0.9, 1, 0.85],
  },
  {
    id: "thapra",
    labelKey: "thapra",
    sales: "87,230",
    delta: "-2.1%",
    orders: "298",
    members: "18",
    week: [0.7, 0.66, 0.58, 0.52, 0.61, 0.88, 1],
  },
];



/** Branches the self-demo visits, in order, over and over. Each lap passes
    back through ทุกสาขา, so the overview is never more than three steps away
    however long the reader watches. */
const TOUR = [1, 2, 3, 0];

/** Slower than the hook's default: this one never stops, and a demo you are
    going to live with needs more room between steps than one that plays once
    and is done. */
const TOUR_GAP = 1600;

export default function DashboardMock({ active }: { active?: boolean }) {
  const { t } = useTranslation();
  /** Weekday initials, Mon→Sun, in the reader's language. */
  const DAYS = t("home.mocks.dashboard.days", {
    returnObjects: true,
  }) as unknown as string[];
  const [at, setAt] = useState(0);

  // The demo drives setAt directly; only the reader's own taps cancel it.
  const { ref: screenRef, cancel } = useSelfDemo<HTMLDivElement>(
    TOUR.map((branch) => () => setAt(branch)),
    { active, loop: true, gap: TOUR_GAP },
  );

  function pick(i: number) {
    cancel();
    setAt(i);
  }

  const branch = BRANCHES[at];
  const down = branch.delta.startsWith("-");
  // The best day gets the yellow bar — the eye lands where the money is.
  const peak = branch.week.indexOf(Math.max(...branch.week));

  return (
    <DeviceStage>
      <IpadFrame>
        <div ref={screenRef} className="flex h-full flex-col bg-[#F4F8FC]">
          {/* App chrome: the brand-blue bar that makes it read as ours. */}
          <div className="flex items-center justify-between bg-[linear-gradient(115deg,#00518D_0%,#0060A8_55%,#0A70BC_100%)] px-[0.7em] py-[0.45em]">
            <span className="flex items-center gap-[0.4em]">
              <Image
                src="/v2/logo.webp"
                alt=""
                width={48}
                height={48}
                className="size-[1em] rounded-full bg-white/90 p-[0.1em]"
              />
              <span className="text-[0.72em] font-semibold text-white">
                {t("home.mocks.dashboard.title")}
              </span>
            </span>
            <span className="flex items-center gap-[0.35em] rounded-full bg-white/12 px-[0.55em] py-[0.2em] text-[0.55em] font-medium text-white">
              <span className="jd-live-blink size-[0.6em] rounded-full bg-[#FFCC33]" />
              {t("home.mocks.dashboard.live")}
            </span>
          </div>

          <div className="flex flex-1 flex-col gap-[0.5em] p-[0.7em]">
            {/* Branch picker — the one control that drives the whole screen.
                The active chip is the yellow "you are here" marker. */}
            <div className="flex gap-[0.3em]">
              {BRANCHES.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => pick(i)}
                  aria-pressed={i === at}
                  className={`rounded-full px-[0.75em] py-[0.35em] text-[0.65em] font-semibold transition-colors ${
                    i === at
                      ? "bg-[#FFCC33] text-[#00518D] shadow-[0_0.15em_0.4em_-0.1em_rgba(255,204,51,0.8)]"
                      : "bg-white text-[#0060A8]/70 shadow-[0_0.1em_0.3em_-0.1em_rgba(0,96,168,0.25)] hover:bg-[#E8F2FA]"
                  }`}
                >
                  {t(`home.mocks.dashboard.branches.${b.labelKey}`)}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-[0.4em]">
              {/* Hero KPI drenched in the brand blue; the delta is the only
                  yellow ink, and only on this dark ground. */}
              <div className="rounded-[0.5em] bg-[linear-gradient(135deg,#0060A8_0%,#0A70BC_100%)] px-[0.55em] py-[0.4em] shadow-[0_0.3em_0.7em_-0.25em_rgba(0,96,168,0.55)]">
                <p className="text-[0.55em] text-white/70">
                  {t("home.mocks.dashboard.salesToday")}
                </p>
                <div className="flex items-baseline gap-[0.4em]">
                  <p
                    key={at}
                    className="ps-mock-pop text-[0.95em] font-semibold text-white"
                  >
                    ฿{branch.sales}
                  </p>
                  <span
                    className={`text-[0.6em] font-semibold ${down ? "text-[#FFB4A8]" : "text-[#FFCC33]"}`}
                  >
                    {branch.delta}
                  </span>
                </div>
              </div>
              <Tile label={t("home.mocks.dashboard.orders")} value={branch.orders} at={at} />
              <Tile label={t("home.mocks.dashboard.newMembers")} value={branch.members} at={at} />
            </div>

            <div className="flex flex-1 flex-col rounded-[0.5em] bg-white p-[0.55em] shadow-[0_0.15em_0.5em_-0.2em_rgba(0,96,168,0.2)]">
              <div className="flex items-center justify-between">
                <p className="text-[0.6em] font-medium text-[#00518D]">
                  {t("home.mocks.dashboard.dailySales")}
                </p>
                <p className="flex items-center gap-[0.3em] text-[0.5em] text-gray-400">
                  <span className="size-[0.65em] rounded-[0.15em] bg-[#FFCC33]" />
                  {t("home.mocks.dashboard.bestDay")}
                </p>
              </div>

              {/* Plot: flex-1 so the chart absorbs whatever height the screen
                  gives this card, with the bars and grid absolutely filling
                  it. min-h keeps the bars readable even in cramped layouts. */}
              <div className="relative mt-[0.45em] min-h-[3.1em] flex-1">
                {/* Dashed grid behind the bars — horizontal rules at each
                    quarter of the scale, vertical ones on the six column
                    boundaries (flex-1 columns, so i/7 of the width), and a
                    solid baseline the bars sit on. */}
                <div aria-hidden className="pointer-events-none absolute inset-0">
                  {[0, 25, 50, 75].map((top) => (
                    <span
                      key={top}
                      className="absolute inset-x-0 border-t border-dashed border-[#0060A8]/15"
                      style={{ top: `${top}%` }}
                    />
                  ))}
                  <span className="absolute inset-x-0 bottom-0 border-t border-[#0060A8]/25" />
                  {Array.from({ length: DAYS.length - 1 }, (_, i) => (
                    <span
                      key={i}
                      className="absolute inset-y-0 border-l border-dashed border-[#0060A8]/10"
                      style={{ left: `${((i + 1) * 100) / DAYS.length}%` }}
                    />
                  ))}
                </div>

                <div className="absolute inset-0 flex items-end gap-[0.35em]">
                  {branch.week.map((h, i) => (
                    <div
                      key={DAYS[i]}
                      className={`flex-1 rounded-t-[0.15em] transition-[height] duration-500 ease-out ${
                        i === peak
                          ? "bg-[linear-gradient(180deg,#FFDA66_0%,#FFC81F_100%)]"
                          : "bg-[linear-gradient(180deg,#2E86C9_0%,#0060A8_100%)]"
                      }`}
                      style={{ height: `${h * 100}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* Day labels in a second row of the same flex-1 columns, so
                  they stay centred under their bars. */}
              <div className="flex gap-[0.35em] pt-[0.25em]">
                {DAYS.map((day, i) => (
                  <span
                    key={day}
                    className={`flex-1 text-center text-[0.5em] ${i === peak ? "font-semibold text-[#00518D]" : "text-gray-400"}`}
                  >
                    {day}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </IpadFrame>
    </DeviceStage>
  );
}

/**
 * `key={at}` restarts the pop animation whenever the branch changes, so the
 * numbers visibly re-read instead of silently swapping.
 */
function Tile({
  label,
  value,
  at,
}: {
  label: string;
  value: string;
  at: number;
}) {
  return (
    <div className="rounded-[0.5em] bg-white px-[0.55em] py-[0.4em] shadow-[0_0.15em_0.5em_-0.2em_rgba(0,96,168,0.2)]">
      <p className="text-[0.55em] text-gray-400">{label}</p>
      <p key={at} className="ps-mock-pop text-[0.85em] font-semibold text-[#00518D]">
        {value}
      </p>
    </div>
  );
}
